import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeftRight, Copy, Eye, Pencil } from 'lucide-react';
import { productService } from '../api/productService';
import { categoryService } from '../api/categoryService';
import Table from '../components/Common/Table';
import Modal from '../components/Common/Modal';
import Pagination from '../components/Common/Pagination';
import SearchBar from '../components/Common/SearchBar';
import ProductForm from '../components/Products/ProductForm';
import ProductStockMovements from '../components/Products/ProductStockMovements';
import '../components/Common/Common.css';
import { brandService } from '../api/brandService';

// Public storefront base URL for the "view product" link (no trailing slash).
const STOREFRONT_URL = (process.env.REACT_APP_STOREFRONT_URL || 'https://tech360.ma').replace(/\/+$/, '');

// Depth-order a flat category list so a multi-level tree shows indented.
function orderCategoriesByDepth(categories) {
  const childrenByParent = new Map();
  categories.forEach((c) => {
    const key = c.parent_id == null ? 'root' : String(c.parent_id);
    if (!childrenByParent.has(key)) childrenByParent.set(key, []);
    childrenByParent.get(key).push(c);
  });
  const out = [];
  const walk = (key, depth) => {
    (childrenByParent.get(key) || []).forEach((cat) => {
      out.push({ ...cat, depth });
      walk(String(cat.id), depth + 1);
    });
  };
  walk('root', 0);
  if (out.length < categories.length) {
    const seen = new Set(out.map((c) => c.id));
    categories.forEach((c) => { if (!seen.has(c.id)) out.push({ ...c, depth: 0 }); });
  }
  return out;
}

function Products() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [brandFilter, setBrandFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [movementsProduct, setMovementsProduct] = useState(null);

  // Inline SKU editing (edit straight from the list, no modal)
  const [editingSkuId, setEditingSkuId] = useState(null);
  const [skuDraft, setSkuDraft] = useState('');
  const [savingSku, setSavingSku] = useState(false);
  const skipSkuBlurRef = useRef(false);

  // Multi-select + bulk edit (category / brand / badges / status)
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkCategory, setBulkCategory] = useState('');
  const [bulkBrand, setBulkBrand] = useState('');
  const [bulkBestSeller, setBulkBestSeller] = useState('');
  const [bulkBestPrice, setBulkBestPrice] = useState('');
  const [bulkVitrine, setBulkVitrine] = useState('');
  const [bulkActive, setBulkActive] = useState('');
  const [bulkQuoteOnly, setBulkQuoteOnly] = useState('');
  const [applyingBulk, setApplyingBulk] = useState(false);

  // Selection is page/filter-scoped — reset it whenever the visible list changes.
  useEffect(() => {
    setSelectedIds([]);
  }, [currentPage, searchTerm, categoryFilter, brandFilter, statusFilter]);

  const toggleRow = (id) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const toggleAll = (checked) =>
    setSelectedIds(checked ? products.map((p) => p.id) : []);

  const clearSelection = () => setSelectedIds([]);

  const resetBulkFields = () => {
    setBulkCategory('');
    setBulkBrand('');
    setBulkBestSeller('');
    setBulkBestPrice('');
    setBulkVitrine('');
    setBulkActive('');
    setBulkQuoteOnly('');
  };

  const handleApplyBulk = async () => {
    if (selectedIds.length === 0) return;

    const updates = {};
    if (bulkCategory !== '') updates.category_id = bulkCategory === 'none' ? null : Number(bulkCategory);
    if (bulkBrand !== '') updates.brand_id = bulkBrand === 'none' ? null : Number(bulkBrand);
    if (bulkBestSeller !== '') updates.is_best_seller = bulkBestSeller === 'on';
    if (bulkBestPrice !== '') updates.is_best_price = bulkBestPrice === 'on';
    if (bulkVitrine !== '') updates.show_on_vetrine_home = bulkVitrine === 'on';
    if (bulkActive !== '') updates.is_active = bulkActive === 'on';
    if (bulkQuoteOnly !== '') updates.is_quote_only = bulkQuoteOnly === 'on';

    if (Object.keys(updates).length === 0) {
      alert('Choisissez au moins une modification à appliquer.');
      return;
    }

    try {
      setApplyingBulk(true);
      await productService.bulkUpdateProducts(selectedIds, updates);
      resetBulkFields();
      setSelectedIds([]);
      fetchProducts();
    } catch (err) {
      console.error('Bulk update failed', err);
      alert(err.response?.data?.message || 'Échec de la mise à jour groupée');
    } finally {
      setApplyingBulk(false);
    }
  };

  const startSkuEdit = (row) => {
    setEditingSkuId(row.id);
    setSkuDraft(row.sku || '');
  };

  const cancelSkuEdit = () => {
    setEditingSkuId(null);
    setSkuDraft('');
  };

  const handleSaveSku = async (row) => {
    const newSku = skuDraft.trim();
    if (newSku === (row.sku || '')) {
      cancelSkuEdit();
      return;
    }
    try {
      setSavingSku(true);
      await productService.updateProduct(row.id, { sku: newSku });
      setProducts((prev) =>
        prev.map((p) => (p.id === row.id ? { ...p, sku: newSku } : p))
      );
      cancelSkuEdit();
    } catch (err) {
      console.error('Failed to update SKU', err);
      alert(err.response?.data?.message || 'Échec de la mise à jour du SKU');
    } finally {
      setSavingSku(false);
    }
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        search: searchTerm,
        per_page: 15,
      };
      if (categoryFilter) params.category_id = categoryFilter;
      if (brandFilter) params.brand_id = brandFilter;
      if (statusFilter) params.is_active = statusFilter;

      const response = await productService.getProducts(params);
      setProducts(response.data);
      setLastPage(response.last_page || 1);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await categoryService.getCategories({ per_page: 100 });
      setCategories(response.data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchBrands = async () => {
    try {
      const response = await brandService.getBrands({ per_page: 100 });
      setBrands(response.data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchBrands();
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [currentPage, searchTerm, categoryFilter, brandFilter, statusFilter]);

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce produit?')) {
      try {
        await productService.deleteProduct(id);
        fetchProducts();
      } catch (error) {
        console.error('Error deleting product:', error);
        alert('Échec de la suppression du produit');
      }
    }
  };

  const handleEdit = async (product) => {
    // The products list does NOT eager-load the `compatibleProducts` relation,
    // so the list row has no `compatible_products`. Opening the form with that
    // partial row would seed the Compatibility tab empty and then wipe all
    // existing links on save (sync([])). Fetch the full product first.
    setEditingProduct(product);
    setShowModal(true);
    try {
      const full = await productService.getProduct(product.id);
      // Guard against the row having changed in the meantime.
      setEditingProduct((current) =>
        current && current.id === product.id ? full : current
      );
    } catch (err) {
      console.error('Failed to load full product for editing:', err);
    }
  };

  const handleCreate = () => {
    setEditingProduct(null);
    setShowModal(true);
  };

  const handleDuplicate = (product) => {
    // Open the create form prefilled with this product's data.
    // Strip identity fields so the user MUST set new ones.
    const cloned = {
      ...product,
      id: undefined,
      slug: '',
      sku: '',
      barcode: '',
      name: `${product.name || ''} (copie)`,
      created_at: undefined,
      updated_at: undefined,
    };
    setEditingProduct(cloned);
    setShowModal(true);
  };

  const handleQuickToggle = async (product, field) => {
    try {
      await productService.updateProduct(product.id, { [field]: !product[field] });
      // Optimistically update the local row so the badge reflects immediately
      setProducts((prev) =>
        prev.map((p) => (p.id === product.id ? { ...p, [field]: !p[field] } : p))
      );
    } catch (err) {
      console.error('Failed to toggle', field, err);
      alert('Échec de la mise à jour');
    }
  };

  const handleFormSuccess = () => {
    setShowModal(false);
    setEditingProduct(null);
    fetchProducts();
  };

  const columns = [
    { key: 'id', label: 'ID' },
    {
      key: 'main_image',
      label: 'Image',
      render: (val) => val ? (
        <img src={val} alt="Product" style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '4px' }} />
      ) : 'Aucune image'
    },
    { key: 'name', label: 'Nom' },
    {
      key: 'sku',
      label: 'SKU',
      render: (val, row) =>
        editingSkuId === row.id ? (
          <input
            type="text"
            value={skuDraft}
            autoFocus
            disabled={savingSku}
            onChange={(e) => setSkuDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                e.target.blur(); // triggers onBlur save
              } else if (e.key === 'Escape') {
                skipSkuBlurRef.current = true;
                cancelSkuEdit();
              }
            }}
            onBlur={() => {
              if (skipSkuBlurRef.current) {
                skipSkuBlurRef.current = false;
                return;
              }
              handleSaveSku(row);
            }}
            placeholder="SKU"
            style={{ width: '130px', padding: '4px 6px', fontSize: '13px' }}
          />
        ) : (
          <span
            onClick={() => startSkuEdit(row)}
            title="Cliquer pour modifier le SKU"
            style={{
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              borderBottom: '1px dashed #cbd5e0',
              whiteSpace: 'nowrap',
            }}
          >
            {val || <span style={{ color: '#a0aec0' }}>—</span>}
            <Pencil size={12} style={{ opacity: 0.45 }} />
          </span>
        ),
    },
    { key: 'category', label: 'Catégorie', render: (val) => val?.name || 'N/A' },
    { key: 'brand', label: 'Marque', render: (val) => val?.name || 'N/A' },
    {
      key: 'price',
      label: 'Prix',
      render: (val, row) =>
        row.is_quote_only ? (
          <span className="badge badge-info" title="Prix sur demande">Par devis</span>
        ) : (
          <div style={{ whiteSpace: 'nowrap' }}>
            <div>{val != null && val !== '' ? `${parseFloat(val).toFixed(2)} DH` : '—'}</div>
            {row.fake_original_price && (
              <small style={{ textDecoration: 'line-through', color: '#a0aec0' }}>
                {parseFloat(row.fake_original_price).toFixed(2)} DH
              </small>
            )}
          </div>
        )
    },
    {
      key: 'stock_quantity',
      label: 'Stock',
      render: (val, row) => {
        const qty = val ?? 0;
        if (qty === 0) {
          return <span className="badge badge-danger">Rupture</span>;
        }
        if (row.is_low_stock) {
          return (
            <span title={`Seuil : ${row.low_stock_threshold ?? 3}`}>
              <span className="badge badge-warning">{qty} (stock bas)</span>
            </span>
          );
        }
        return <span style={{ color: '#22543d', fontWeight: 500 }}>{qty}</span>;
      },
    },
    {
      key: 'media',
      label: 'Média',
      render: (val) => val?.length || 0
    },
    {
      key: 'is_best_seller',
      label: 'Badges (cliquer pour basculer)',
      render: (val, row) => {
        const toggleStyle = (active) => ({
          cursor: 'pointer',
          opacity: active ? 1 : 0.35,
          userSelect: 'none',
        });
        return (
          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
            <span
              className="badge badge-warning"
              style={toggleStyle(row.is_best_seller)}
              onClick={() => handleQuickToggle(row, 'is_best_seller')}
              title="Basculer Meilleure vente"
            >
              Meilleure vente
            </span>
            <span
              className="badge badge-info"
              style={toggleStyle(row.is_best_price)}
              onClick={() => handleQuickToggle(row, 'is_best_price')}
              title="Basculer Meilleur prix"
            >
              Meilleur prix
            </span>
            <span
              className="badge badge-success"
              style={toggleStyle(row.show_on_vetrine_home)}
              onClick={() => handleQuickToggle(row, 'show_on_vetrine_home')}
              title="Basculer Vitrine"
            >
              Vitrine
            </span>
          </div>
        );
      },
    },
    {
      key: 'is_active',
      label: 'Statut',
      render: (val) => (
        <span className={`badge ${val ? 'badge-success' : 'badge-danger'}`}>
          {val ? 'Actif' : 'Inactif'}
        </span>
      ),
    },
  ];

  return (
    <div>
      <div className="page-header">
        <h1>Produits</h1>
        <button onClick={handleCreate} className="btn btn-primary">
          Ajouter un produit
        </button>
      </div>

      <div className="filters">
        <div className="filters-grid">
          <div className="form-group">
            <label>Recherche</label>
            <SearchBar
              value={searchTerm}
              onChange={(val) => { setSearchTerm(val); setCurrentPage(1); }}
              placeholder="Rechercher par nom, SKU, code-barres..."
              onClear={() => setCurrentPage(1)}
            />
          </div>
          <div className="form-group">
            <label>Filtrer par catégorie</label>
            <select value={categoryFilter} onChange={(e) => { setCategoryFilter(e.target.value); setCurrentPage(1); }}>
              <option value="">Toutes les catégories</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Filtrer par marque</label>
            <select value={brandFilter} onChange={(e) => { setBrandFilter(e.target.value); setCurrentPage(1); }}>
              <option value="">Toutes les marques</option>
              {brands.map((brand) => (
                <option key={brand.id} value={brand.id}>{brand.name}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Filtrer par statut</label>
            <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}>
              <option value="">Tous les statuts</option>
              <option value="1">Actif</option>
              <option value="0">Inactif</option>
            </select>
          </div>
        </div>
      </div>

      {selectedIds.length > 0 && (
        <div className="filters" style={{ borderLeft: '4px solid #3182ce' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
            <strong>{selectedIds.length} produit(s) sélectionné(s) — modification groupée</strong>
            <button type="button" className="btn btn-secondary" onClick={clearSelection}>
              Désélectionner
            </button>
          </div>
          <div className="filters-grid">
            <div className="form-group">
              <label>Catégorie</label>
              <select value={bulkCategory} onChange={(e) => setBulkCategory(e.target.value)}>
                <option value="">— Ne pas changer —</option>
                <option value="none">Aucune catégorie</option>
                {orderCategoriesByDepth(categories).map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {`${'   '.repeat(cat.depth)}${cat.depth > 0 ? '└ ' : ''}${cat.name}`}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Marque</label>
              <select value={bulkBrand} onChange={(e) => setBulkBrand(e.target.value)}>
                <option value="">— Ne pas changer —</option>
                <option value="none">Aucune marque</option>
                {brands.map((brand) => (
                  <option key={brand.id} value={brand.id}>{brand.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Meilleure vente</label>
              <select value={bulkBestSeller} onChange={(e) => setBulkBestSeller(e.target.value)}>
                <option value="">— Ne pas changer —</option>
                <option value="on">Activer</option>
                <option value="off">Désactiver</option>
              </select>
            </div>
            <div className="form-group">
              <label>Meilleur prix</label>
              <select value={bulkBestPrice} onChange={(e) => setBulkBestPrice(e.target.value)}>
                <option value="">— Ne pas changer —</option>
                <option value="on">Activer</option>
                <option value="off">Désactiver</option>
              </select>
            </div>
            <div className="form-group">
              <label>Vitrine</label>
              <select value={bulkVitrine} onChange={(e) => setBulkVitrine(e.target.value)}>
                <option value="">— Ne pas changer —</option>
                <option value="on">Activer</option>
                <option value="off">Désactiver</option>
              </select>
            </div>
            <div className="form-group">
              <label>Statut</label>
              <select value={bulkActive} onChange={(e) => setBulkActive(e.target.value)}>
                <option value="">— Ne pas changer —</option>
                <option value="on">Actif</option>
                <option value="off">Inactif</option>
              </select>
            </div>
            <div className="form-group">
              <label>Par devis</label>
              <select value={bulkQuoteOnly} onChange={(e) => setBulkQuoteOnly(e.target.value)}>
                <option value="">— Ne pas changer —</option>
                <option value="on">Activer (prix sur demande)</option>
                <option value="off">Désactiver</option>
              </select>
            </div>
          </div>
          <div style={{ marginTop: '12px' }}>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleApplyBulk}
              disabled={applyingBulk}
            >
              {applyingBulk ? 'Application...' : `Appliquer à ${selectedIds.length} produit(s)`}
            </button>
          </div>
        </div>
      )}

      <Table
        columns={columns}
        data={products}
        loading={loading}
        onEdit={handleEdit}
        onDelete={handleDelete}
        selectable
        selectedIds={selectedIds}
        onToggleRow={toggleRow}
        onToggleAll={toggleAll}
        customActions={(row) => (
          <>
            <a
              href={`${STOREFRONT_URL}/product/?slug=${encodeURIComponent(row.slug || '')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="icon-btn"
              title="Voir sur le site"
              aria-label="Voir sur le site"
            >
              <Eye size={16} />
            </a>
            <button
              type="button"
              onClick={() => setMovementsProduct(row)}
              className="icon-btn icon-btn--accent"
              title="Mouvements de stock"
              aria-label="Mouvements de stock"
            >
              <ArrowLeftRight size={16} />
            </button>
            <button
              type="button"
              onClick={() => handleDuplicate(row)}
              className="icon-btn"
              title="Dupliquer le produit"
              aria-label="Dupliquer le produit"
            >
              <Copy size={16} />
            </button>
          </>
        )}
      />

      <Pagination
        currentPage={currentPage}
        lastPage={lastPage}
        onPageChange={setCurrentPage}
      />

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={
          editingProduct?.id
            ? 'Modifier le produit'
            : editingProduct
            ? 'Dupliquer un produit (ajustez le slug, SKU, et code-barres avant d\'enregistrer)'
            : 'Ajouter un produit'
        }
        size="fullscreen"
        closeOnOverlayClick={false}
      >
        <ProductForm
          product={editingProduct}
          onSuccess={handleFormSuccess}
          onCancel={() => setShowModal(false)}
        />
      </Modal>

      <Modal
        isOpen={!!movementsProduct}
        onClose={() => setMovementsProduct(null)}
        title={movementsProduct ? `Mouvements de stock — ${movementsProduct.name}` : 'Mouvements de stock'}
        size="large"
      >
        {movementsProduct && (
          <ProductStockMovements
            product={movementsProduct}
            onChanged={fetchProducts}
          />
        )}
      </Modal>
    </div>
  );
}

export default Products;
